/**
 * Axios API Client with Interceptors
 * 
 * Features:
 * - Auto-appends /api prefix to all requests (prevents production routing bugs)
 * - Auto-attaches auth tokens (admin or staff)
 * - Handles environment-specific base URLs
 * - Consistent error handling
 * 
 * USAGE:
 * import { api, staffApi } from '@/utils/apiClient';
 * 
 * // For admin API calls:
 * const response = await api.get('/admin/settings');
 * const response = await api.post('/admin/menu/items', data);
 * 
 * // For staff portal API calls:
 * const response = await staffApi.get('/staff-portal/profile', { params: { token } });
 * 
 * // For public API calls (no auth):
 * const response = await api.get('/menu/items');
 */

import axios from 'axios';

// Determine base URL based on environment
const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // In development (localhost), use the env variable
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || '';
    }
    
    // For ALL deployed environments (preview, production)
    // Return origin (same domain) - ingress routes /api to backend
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

// Create the main Axios instance
const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - auto-append /api prefix
api.interceptors.request.use(
  (config) => {
    // Ensure URL starts with /api
    if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
      config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    
    // Auto-attach admin auth token if available
    const adminData = localStorage.getItem('bulaccino-admin');
    if (adminData && !config.headers['Authorization']) {
      try {
        const parsed = JSON.parse(adminData);
        const token = parsed.access_token || parsed.token || '';
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn('[API] Failed to parse admin token');
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors with centralized 401 redirect
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      console.warn('[API] Unauthorized request:', error.config?.url);
      
      // Check if we're not already on login page to prevent redirect loop
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === '/admin' || currentPath === '/login' || currentPath === '/admin/login';
      
      // Pages that handle their own authentication (PIN-based)
      const isPinAuthPage = currentPath === '/server' || currentPath === '/pos' || currentPath === '/kiosk' || currentPath.startsWith('/server') || currentPath.startsWith('/pos');
      
      // Check if user has admin auth (meaning they were logged in but token expired)
      const hasAdminAuth = !!localStorage.getItem('bulaccino-admin');
      
      // Redirect for admin routes if user was previously authenticated
      // This covers /admin/*, /mistakes/*, /workforce/*, /devices/*, /finance/* etc.
      // But NOT for PIN-auth pages which handle their own session
      if (!isAuthPage && !isPinAuthPage && hasAdminAuth) {
        // Clear stale auth data
        localStorage.removeItem('bulaccino-admin');
        
        // Show notification if toast is available (sonner)
        if (window.showToast) {
          window.showToast('Session expired. Please login again.', { type: 'error' });
        }
        
        // Redirect to admin login after brief delay
        setTimeout(() => {
          window.location.href = '/admin';
        }, 100);
      }
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('[API] Network error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Staff Portal API client (uses token in query params for staff portal)
const staffApi = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Staff API request interceptor
staffApi.interceptors.request.use(
  (config) => {
    // Ensure URL starts with /api
    if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
      config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    
    // Auto-attach staff token from localStorage if not already in params
    const staffToken = localStorage.getItem('staff_token');
    if (staffToken && !config.params?.token) {
      config.params = { ...config.params, token: staffToken };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// POS API client (uses staff PIN session)
const posApi = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// POS API request interceptor
posApi.interceptors.request.use(
  (config) => {
    // Ensure URL starts with /api
    if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
      config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    
    // Auto-attach POS staff session token if available
    const posSession = localStorage.getItem('pos_session') || sessionStorage.getItem('pos_session');
    if (posSession) {
      try {
        const session = JSON.parse(posSession);
        if (session.staff_id) {
          config.headers['X-Staff-ID'] = session.staff_id;
        }
        if (session.session_token) {
          config.headers['X-POS-Session'] = session.session_token;
        }
      } catch (e) {
        console.warn('[POS API] Failed to parse POS session');
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function for backwards compatibility with existing fetch calls
export const apiFetch = async (url, options = {}) => {
  const fullUrl = url.startsWith('/api') ? url : `/api${url.startsWith('/') ? '' : '/'}${url}`;
  
  const config = {
    method: options.method || 'GET',
    url: fullUrl,
    headers: options.headers || {},
    data: options.body ? JSON.parse(options.body) : undefined,
  };
  
  try {
    const response = await api(config);
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      json: async () => response.data,
      data: response.data,
    };
  } catch (error) {
    return {
      ok: false,
      status: error.response?.status || 500,
      json: async () => error.response?.data || { error: error.message },
      data: error.response?.data,
    };
  }
};

export { api, staffApi, posApi };
export default api;
